'use client';

import { Loader2, Search } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { type ReactNode, useRef } from 'react';
import { Container } from '../../components/container';
import { CustomLink } from '../../components/custom-link';
import { cn, radixOverlayStyles } from '../../lib';
import { useLayoutPosition } from '../../lib/hooks/use-layout-position';

function SearchDialogRoot({
  children,
  open,
  onOpenChange,
}: {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <div
        className={cn(
          'search group pointer-events-none fixed inset-x-0 bottom-0 z-[101]',
          open && 'pointer-events-auto'
        )}
      >
        {children}
      </div>
    </DialogPrimitive.Root>
  );
}

function SearchDialogContent({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const { totalOffset } = useLayoutPosition();

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn('fixed inset-0 z-50', radixOverlayStyles)}
        style={{ top: `${totalOffset}px` }}
      />
      <DialogPrimitive.Content
        className={cn(
          'fixed inset-x-0 z-50 w-screen overflow-hidden bg-background data-[state=open]:border-y',
          'data-[state=closed]:animate-slide-out-to-top data-[state=open]:animate-slide-in-from-top',
          className
        )}
        style={{ top: `${totalOffset}px` }}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="relative">{children}</div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

function SearchDialogHeader({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <header
      className={cn(
        'relative z-10 flex h-[--header-height] min-h-[80px] items-center justify-between gap-4 py-2',
        className
      )}
    >
      {children}
    </header>
  );
}

function SearchDialogInput({
  value,
  onChange,
  isLoading,
  className,
  onSubmit,
}: {
  value: string;
  onChange: (value: string) => void;
  isLoading?: boolean;
  className?: string;
  onSubmit?: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const id = 'search-input';

  return (
    <div className="flex-1">
      <label
        htmlFor={id}
        className={cn('flex w-full items-center gap-4', className)}
      >
        {isLoading ? (
          <Loader2 className="size-5 animate-spin" />
        ) : (
          <Search className="size-5" />
        )}
        <input
          id={id}
          ref={inputRef}
          type="text"
          placeholder="Search..."
          className="w-full py-2 text-inherit outline-none placeholder:text-inherit"
          autoFocus
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              if (onSubmit && value.trim()) {
                onSubmit();
              }
            }
          }}
        />
      </label>
    </div>
  );
}

function SearchDialogResults({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <main
      className={cn(
        'relative grid gap-16 overflow-hidden pt-10 pb-14',
        className
      )}
    >
      <section className="flex-1">{children}</section>
    </main>
  );
}

function SearchDialogResultsHeader({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <header className="flex w-full items-baseline justify-between border-inherit border-b pb-3.5">
      {children}
    </header>
  );
}

function SearchDialogResultsTitle({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <DialogPrimitive.Title className={cn('', className)}>
      {children}
    </DialogPrimitive.Title>
  );
}

function SearchDialogResultsViewAll({
  children,
  query,
  className,
}: {
  children: ReactNode;
  query?: string;
  className?: string;
}) {
  return (
    <CustomLink
      href={query ? `/search?q=${query}` : '/search'}
      className={cn('hf:underline', className)}
    >
      {children}
    </CustomLink>
  );
}

function SearchDialogResultsGrid({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <main className="mt-9 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
      {children}
    </main>
  );
}

export function SearchDialogShared({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={className}>{children}</div>;
}

SearchDialogShared.Root = SearchDialogRoot;
SearchDialogShared.Content = SearchDialogContent;
SearchDialogShared.Header = SearchDialogHeader;
SearchDialogShared.Input = SearchDialogInput;
SearchDialogShared.Results = SearchDialogResults;
SearchDialogShared.ResultsHeader = SearchDialogResultsHeader;
SearchDialogShared.ResultsTitle = SearchDialogResultsTitle;
SearchDialogShared.ResultsViewAll = SearchDialogResultsViewAll;
SearchDialogShared.ResultsGrid = SearchDialogResultsGrid;
SearchDialogShared.Close = DialogPrimitive.Close;
SearchDialogShared.Container = Container;
